// @ts-check
// variance-engine.jsx — Module 1: variance flagging with tolerance configuration
import { TradeSectionHeader } from './invodiff-jobs.jsx';
import { MatchAuditView } from './match-audit.jsx';
import React from 'react';
import { firebase, fbAuth, fbDb, fbStorage } from './firebase.js';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';
import { DocProvenanceModal, primaryPreviewSource } from './doc-provenance.jsx';

// Matching helpers (levenshtein, fracToNum, normalizeForMatch) and the
// matchLineItems pipeline now live in invodiff-data.jsx — the single source of
// truth for pairing quote lines to invoice lines. They're available as globals
// because that file loads first.

// ── Variance constants ──────────────────────────────────────────────────────
const DEFAULT_TOLERANCES = {
  quantity_tolerance_pct: 5,
  price_tolerance_pct: 3,
  price_tolerance_flat: 500,
  footage_tolerance_pct: 5,
};

const FOOTAGE_UOMS = new Set(['LF', 'SF', 'SY']);

const VARIANCE_META = {
  WITHIN_TOLERANCE: { label: 'OK',        tone: 'ok',       pulse: false, short: '✓' },
  SOFT_FLAG:        { label: 'Soft Flag',  tone: 'warn',     pulse: false, short: '~' },
  HARD_FLAG:        { label: 'Hard Flag',  tone: 'bad',      pulse: false, short: '!' },
  CRITICAL:         { label: 'Critical',   tone: 'critical', pulse: true,  short: '!!' },
};

// The default UI speaks in three plain states; the four-level severity above is
// the internal detail (kept as a tooltip / sort key). This map is the bridge.
const VARIANCE_LEVEL = {
  WITHIN_TOLERANCE: { level: 'good',    label: 'Good',         tone: 'ok'   },
  SOFT_FLAG:        { level: 'review',  label: 'Needs Review', tone: 'warn' },
  HARD_FLAG:        { level: 'problem', label: 'Problem',      tone: 'bad'  },
  CRITICAL:         { level: 'problem', label: 'Problem',      tone: 'bad'  },
};

// ── Classify a row against tolerance thresholds ─────────────────────────────
function classifyVariance(row, tols) {
  const { quantity_tolerance_pct, price_tolerance_pct, price_tolerance_flat, footage_tolerance_pct } = tols;

  if (row.flags.missing) return 'WITHIN_TOLERANCE';

  // Issues reflects a job IN PROGRESS (product decision, 2026-08-03). That makes
  // this classifier DIRECTIONAL: over-delivery and over-spend are problems,
  // under-delivery and under-spend are not. A line only partly drawn down is the
  // normal mid-job state — the rest simply has not been bought yet — and an
  // off-quote CREDIT is money coming back, not an overcharge. Judging either by
  // magnitude (the old Math.abs) reported ordinary progress as CRITICAL.
  if (row.flags.added) {
    // An off-quote credit refunds money; only a positive charge is exposure.
    const offQuoteCharge = Math.max(0, row.invoicedTotal || 0);
    if (offQuoteCharge > 10000) return 'CRITICAL';
    if (offQuoteCharge > 1000)  return 'HARD_FLAG';
    return offQuoteCharge > 0 ? 'SOFT_FLAG' : 'WITHIN_TOLERANCE';
  }

  // Quote and invoice bill on units we couldn't reconcile — the qty/price
  // numbers are not comparable, so this always needs a human look.
  if (row.flags.uom) return 'HARD_FLAG';

  // Overspend only. A negative delta is money not yet spent against this line
  // (or a favourable price), neither of which is a variance on a live job.
  const overSpend = Math.max(0, row.delta || 0);
  if (overSpend > 10000) return 'CRITICAL';

  const isFootage = FOOTAGE_UOMS.has(row.uomNorm) || row.quotedLF != null;
  const priceTolPct = isFootage ? footage_tolerance_pct : price_tolerance_pct;

  // Over-pull only. Quoted 100 / invoiced 40 is a partial drawdown, not a
  // shortage — you can still buy the remaining 60. You cannot un-buy an
  // over-pull, so that direction still counts.
  const qtyDeltaPct = (row.quotedQty > 0 && row.invoicedQty > row.quotedQty)
    ? (row.invoicedQty - row.quotedQty) / row.quotedQty * 100
    : 0;
  const priceDeltaPct = row.quotedTotal > 0
    ? overSpend / row.quotedTotal * 100
    : 0;

  const qtyExceeds   = qtyDeltaPct   > quantity_tolerance_pct;
  const pricePctEx   = priceDeltaPct > priceTolPct;
  const priceFlatEx  = overSpend     > price_tolerance_flat;
  const priceExceeds = pricePctEx || priceFlatEx;

  if (!qtyExceeds && !priceExceeds) return 'WITHIN_TOLERANCE';
  if (qtyExceeds && priceExceeds)   return 'CRITICAL';

  const qtyFactor   = quantity_tolerance_pct > 0 ? qtyDeltaPct   / quantity_tolerance_pct : 0;
  const pctFactor   = priceTolPct            > 0 ? priceDeltaPct / priceTolPct            : 0;
  const flatFactor  = price_tolerance_flat   > 0 ? overSpend     / price_tolerance_flat   : 0;

  if (qtyFactor >= 2 || pctFactor >= 2 || flatFactor >= 2) return 'HARD_FLAG';
  return 'SOFT_FLAG';
}

// ── Enhanced variance computation ───────────────────────────────────────────
// Pairs lines via the shared matchLineItems, then classifies each row against
// tolerance thresholds.
function computeVariances(quote, invoices, tolerances, rules = []) {
  if (!quote || !invoices) return { rows: [], summary: { totalExposure: 0, flaggedCount: 0, byStatus: {}, rowCount: 0 } };
  const tols = { ...DEFAULT_TOLERANCES, ...(tolerances || {}) };

  // Pairing is delegated to the shared matchLineItems (invodiff-data.jsx) so the
  // variance view and the budget tracker always agree on what matched what.
  // Vendor alias rules (learned manual links) apply as the strongest pass.
  const pairs = matchLineItems(quote, invoices, {
    aliases: rules.filter((r) => r.kind === 'alias'),
  });

  // Same rule-matching as aggregateInvoices: by SKU pattern, or by the stable
  // item key for the per-item "accept" written from the exceptions desk.
  const ruleMatchesSku = (rule, s) =>
    rule.pattern === '*' || rule.pattern === s || (!!s && s !== '—' && s.startsWith(rule.pattern));

  // Build rows — invoice lines are folded onto the quote's unit basis by the
  // shared summarizeMatch (per-C/per-M pricing, stick↔LF, box packs).
  const rows = pairs.map((pair) => {
    const { quote: qi, iis, matchType } = pair;
    const uomNorm     = normalizeUOM(qi?.uom || iis[0]?.uom || null);
    const quotedQty   = qi?.qty ?? null;
    const quotedUnit  = qi?.unit ?? null;
    const quotedTotal = qi ? +(qi.qty * qi.unit).toFixed(2) : 0;

    const {
      breakdown, invoicedQty, invoicedLineSum, invoicedAvgUnit, invoicedUOM,
      uomMismatch, quotedLF, invoicedLF,
    } = summarizeMatch(qi, iis);
    const delta = +(invoicedLineSum - quotedTotal).toFixed(2);

    const comparable = qi && iis.length > 0 && !uomMismatch;
    const flags = {
      price:   comparable && pricesDiffer(invoicedAvgUnit, quotedUnit),
      qty:     comparable && invoicedQty < (quotedQty || 0),
      over:    comparable && invoicedQty > (quotedQty || 0),
      uom:     !!(qi && iis.length > 0 && uomMismatch),
      missing: matchType === 'missing_from_invoice',
      added:   matchType === 'new_charge',
    };

    // Honor learned "ignore"/"tolerance" rules so an exception accepted from the
    // desk stops re-appearing here — mirrors aggregateInvoices. Without this the
    // Issues view never clears what the user accepted ("Accept as off-quote").
    const sku0    = qi?.sku || iis[0]?.sku || '—';
    const anyFlag = flags.added || flags.price || flags.qty || flags.over || flags.missing || flags.uom;
    const itemKey = qi ? itemKeyOf(qi) : (iis[0] ? itemKeyOf(iis[0]) : null);
    let resolved = false, resolvedBy = null;
    for (const r of rules) {
      const appliesBySku = ruleMatchesSku(r, sku0);
      const appliesByKey = !!r.quoteKey && !!itemKey && r.quoteKey === itemKey;
      if (!appliesBySku && !appliesByKey) continue;
      if (r.kind === 'ignore' && anyFlag) { resolved = true; resolvedBy = r; break; }
      if (r.kind === 'tolerance' && appliesBySku && flags.price && !flags.qty && !flags.over && !flags.added && !flags.missing
          && quotedUnit > 0 && Math.abs((invoicedAvgUnit - quotedUnit) / quotedUnit) <= 0.03) {
        resolved = true; resolvedBy = r; break;
      }
    }

    const row = {
      key:          qi?._id || iis[0]?._id || Math.random().toString(36).slice(2),
      sku:          sku0,
      desc:         qi?.desc || iis[0]?.desc || '',
      quoteItem:    qi || null,
      invoiceDesc:  iis.length > 0 ? (iis[0]?.desc || null) : null,
      invSku:       iis.length > 0 ? (iis[0]?.sku  || null) : null,
      cost_code:    qi?.cost_code || iis[0]?.cost_code || null,
      phase:        qi?.phase   || iis[0]?.phase   || null,
      uomNorm, invoicedUOM, quotedLF, invoicedLF,
      quotedQty, quotedUnit, quotedTotal,
      invoicedQty, invoicedTotal: invoicedLineSum, invoicedAvgUnit,
      delta, flags, matchType, matchScore: pair.matchScore,
      matchNote: pair.matchNote, ruleId: pair.ruleId,
      matchReason: pair.matchReason, confidence: pair.confidence,
      lowConfidence: pair.lowConfidence, candidates: pair.candidates,
      breakdown, resolved, resolvedBy,
    };
    // A resolved row reads as within tolerance everywhere — banner, counts, table.
    row.varianceStatus = resolved ? 'WITHIN_TOLERANCE' : classifyVariance(row, tols);
    return row;
  });

  // Summary
  const flagged = rows.filter((r) => r.varianceStatus !== 'WITHIN_TOLERANCE');
  const byStatus = {
    WITHIN_TOLERANCE: 0, SOFT_FLAG: 0, HARD_FLAG: 0, CRITICAL: 0,
  };
  for (const r of rows) byStatus[r.varianceStatus] = (byStatus[r.varianceStatus] || 0) + 1;

  return {
    rows,
    summary: {
      rowCount:      rows.length,
      flaggedCount:  flagged.length,
      totalExposure: +flagged.reduce((s, r) => s + Math.abs(r.delta), 0).toFixed(2),
      byStatus,
    },
  };
}

// ── VarianceBadge ───────────────────────────────────────────────────────────
function VarianceBadge({ status }) {
  const v = VARIANCE_LEVEL[status] || VARIANCE_LEVEL.SOFT_FLAG;
  const raw = VARIANCE_META[status];
  // Critical still pulses for urgency, but it reads as "Problem" like the rest.
  const pulse = status === 'CRITICAL';
  return (
    <span className={`vbadge vbadge-${v.tone}${pulse ? ' vbadge-pulse' : ''}`} title={raw ? raw.label : ''}>
      {v.label}
    </span>
  );
}

// Status pill driven by the canonical material state (Good / Needs Review /
// Problem) — same three buckets every other screen shows.
const LEVEL_BADGE = {
  good:    { tone: 'ok',   label: 'Good' },
  review:  { tone: 'warn', label: 'Needs Review' },
  problem: { tone: 'bad',  label: 'Problem' },
};
function LevelBadge({ state }) {
  const v = LEVEL_BADGE[state?.level] || LEVEL_BADGE.review;
  return (
    <span className={`vbadge vbadge-${v.tone}`} title={(state?.reasons || []).join(' · ')}>
      {v.label}
    </span>
  );
}

// ── VarianceSummaryBanner ───────────────────────────────────────────────────
// Counts come straight from the canonical classifier (problem/review/exposure)
// so this header always equals the Exceptions list and the dashboard badge.
// A PM doesn't need the per-category bar or a separate "biggest exposure" list —
// that detail just repeated the actionable list below it. The banner is now a
// single plain-language summary line: how many items, and the total dollars that
// differ from the quote. The list underneath is where the work happens.
function VarianceSummaryBanner({ problem, review, exposure }) {
  const open  = problem + review;
  const allOk = open === 0;
  if (allOk) {
    return (
      <div className="vsb vsb-ok">
        <div className="vsb-main">
          <Icon name="check" size={16} /><span className="vsb-ok-txt">Everything matches the quote — nothing to review.</span>
        </div>
      </div>
    );
  }
  return (
    <div className={`vsb ${problem > 0 ? 'vsb-critical' : 'vsb-flagged'}`}>
      <div className="vsb-main">
        <div className="vsb-stat">
          <span className="vsb-v num">{open}</span>
          <span className="vsb-l">item{open !== 1 ? 's' : ''} to review</span>
        </div>
        <div className="vsb-sep" />
        <div className="vsb-stat">
          <span className="vsb-v num">{fmtUSD(exposure)}</span>
          <span className="vsb-l">total difference from quote</span>
        </div>
        <div className="vsb-chips">
          {problem > 0 && <span className="vsb-chip vsb-chip-bad">{problem} to fix</span>}
          {review  > 0 && <span className="vsb-chip vsb-chip-warn">{review} to double-check</span>}
        </div>
      </div>
    </div>
  );
}

// ── Exceptions desk ─────────────────────────────────────────────────────────
// One place to clear everything that needs a human: invoice lines that didn't
// match a material, matches the engine isn't sure about, materials billed past
// budget, and quantities pulled past the RFQ. Each row carries the action that
// resolves it (confirm a match, accept an overage, or open the audit to link).
function ExcGroup({ tone, title, count, note, children }) {
  return (
    <div className={`exc-grp exc-grp-${tone}`}>
      <div className="exc-grp-hd">
        <span className="exc-grp-dot" />
        <span className="exc-grp-t">{title}</span>
        <span className="exc-grp-c num">{count}</span>
        <span className="exc-grp-note">{note}</span>
      </div>
      <div className="exc-grp-body">{children}</div>
    </div>
  );
}

// `row` + the two documents are optional: when the source PDF is reachable the
// item grows a "See source" button that opens the original page at this exact
// line. It sits BEFORE the resolve actions on purpose — the point of the
// exceptions desk is to look at the document and then decide, and accepting a
// charge you haven't looked at is the failure mode this whole screen exists to
// prevent.
function ExcItem({ children, actions, row, quote, invoices, onPreview }) {
  const src = React.useMemo(
    () => (row && onPreview ? primaryPreviewSource(row, quote, invoices) : null),
    [row, quote, invoices, onPreview]
  );
  return (
    <div className="exc-item">
      <div className="exc-item-main">{children}</div>
      <div className="exc-item-acts">
        {src && (
          <button className="exc-btn exc-btn-src" type="button"
            onClick={() => onPreview(src.doc, src.sel)}
            title="Open the original document at this line">
            <Icon name="search" size={11} /> See source
          </button>
        )}
        {actions}
      </div>
    </div>
  );
}

// Per-row reason chip for the price/quantity-problem bucket. Pure (depends only
// on its argument), so it lives at module scope rather than being rebuilt each render.
const issueOf = (r) => {
  if (r.flags.over) return 'Billed more than ordered';
  if (r.flags.qty)  return 'Billed less than ordered';
  if (r.flags.price && r.invoicedAvgUnit > r.quotedUnit) return 'Price went up';
  if (r.flags.price) return 'Price changed';
  return "Doesn't match the quote";
};

// Exception bucket keys, in display order. The list itself is static, so it
// lives at module scope; the matching accumulator inside the component must be
// rebuilt each render (its arrays collect that render's open rows) and so is
// derived fresh from this list rather than hoisted.
const EXC_BUCKETS = ['unmatched', 'overPull', 'uom', 'problem', 'review'];

function ExceptionsPanel({ rows, states, quote, invoices, onPreview, onConfirm, onAccept, onOpenAudit }) {
  // Every OPEN row (problem or review per the canonical classifier) lands in
  // exactly one bucket below, so the bucket counts always sum to the header
  // count — and "Problem" price/short items are now actionable, not just the
  // unmatched ones. resolved rows classify as 'good' and drop out automatically.
  const open = rows.filter((r) => !r.synthetic && states.get(r) && states.get(r).level !== 'good');
  const lvl  = (r) => states.get(r).level;

  const b = Object.fromEntries(EXC_BUCKETS.map((k) => [k, []]));
  for (const r of open) {
    if (r.flags.added) b.unmatched.push(r);
    else if (r.quotedQty != null && r.invoicedQty != null && r.invoicedQty > r.quotedQty) b.overPull.push(r);
    else if (r.flags.uom) b.uom.push(r);
    else if (lvl(r) === 'problem') b.problem.push(r);
    else b.review.push(r);
  }
  const total = open.length;
  // Same four props on every bucket's items — spread rather than repeated, so a
  // new bucket can't quietly ship without the source button.
  const srcProps = (r) => ({ row: r, quote, invoices, onPreview });

  if (total === 0) {
    return (
      <div className="exc exc-clear">
        <Icon name="check" size={18} />
        <div>
          <div className="exc-clear-h">Nothing needs your attention</div>
          <div className="exc-clear-s">Every invoice line matched a quoted item and the prices and amounts line up.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="exc">
      <div className="exc-hd">
        <Icon name="flag" size={14} />
        <span className="exc-hd-t">What needs your attention</span>
        <span className="exc-hd-c num">{total}</span>
        <span className="exc-hd-note">Pick an action on each row to clear it.</span>
      </div>

      {b.unmatched.length > 0 && (
        <ExcGroup tone="bad" title="Charged for items that weren't quoted" count={b.unmatched.length}
          note="These were billed but don't match anything on your quote.">
          {b.unmatched.map((r) => (
            <ExcItem key={r.key} {...srcProps(r)} actions={<>
              <button className="exc-btn exc-btn-primary" onClick={onOpenAudit}>Link to a quoted item</button>
              <button className="exc-btn" onClick={() => onAccept(r)}>It's fine, accept it</button>
            </>}>
              <span className="exc-desc" title={r.desc}>{r.desc}</span>
              {r.sku && r.sku !== '—' && <span className="exc-sku mono">{r.sku}</span>}
              <span className="exc-amt num">{fmtUSD(r.invoicedTotal)}</span>
            </ExcItem>
          ))}
        </ExcGroup>
      )}

      {b.overPull.length > 0 && (
        <ExcGroup tone="bad" title="Billed more than was ordered" count={b.overPull.length}
          note="The invoice quantity is higher than what the quote called for.">
          {b.overPull.map((r) => {
            const rem = +(r.quotedQty - r.invoicedQty).toFixed(2);
            const uom = r.uomNorm || '';
            return (
              <ExcItem key={r.key} {...srcProps(r)} actions={<button className="exc-btn" onClick={() => onAccept(r)}>It's fine, accept it</button>}>
                <span className="exc-desc" title={r.desc}>{r.desc}</span>
                <span className="exc-qty num">{r.quotedQty.toLocaleString()} ordered · {r.invoicedQty.toLocaleString()} billed</span>
                <span className="exc-delta num">{rem.toLocaleString()} {uom}</span>
              </ExcItem>
            );
          })}
        </ExcGroup>
      )}

      {b.problem.length > 0 && (
        <ExcGroup tone="bad" title="Price or amount doesn't match the quote" count={b.problem.length}
          note="These matched a quoted item, but the price or quantity is off by more than the allowed range.">
          {b.problem.map((r) => (
            <ExcItem key={r.key} {...srcProps(r)} actions={<>
              <button className="exc-btn exc-btn-primary" onClick={onOpenAudit}>See details</button>
              <button className="exc-btn" onClick={() => onAccept(r)}>It's fine, accept it</button>
            </>}>
              <span className="exc-desc" title={r.desc}>{r.desc}</span>
              <span className="exc-issue">{issueOf(r)}</span>
              <span className="exc-amt num">{fmtUSD(r.quotedTotal)} → {fmtUSD(r.invoicedTotal)}</span>
              {r.delta !== 0 && <span className="exc-delta num">{r.delta > 0 ? '+' : '−'}{fmtUSD(Math.abs(r.delta))}</span>}
            </ExcItem>
          ))}
        </ExcGroup>
      )}

      {b.uom.length > 0 && (
        <ExcGroup tone="warn" title="Units don't match — can't compare" count={b.uom.length}
          note="The quote and invoice use different units, so we can't check the price automatically.">
          {b.uom.map((r) => (
            <ExcItem key={r.key} {...srcProps(r)} actions={<>
              <button className="exc-btn exc-btn-primary" onClick={onOpenAudit}>Re-link</button>
              <button className="exc-btn" onClick={() => onAccept(r)}>It's fine, accept it</button>
            </>}>
              <span className="exc-desc" title={r.desc}>{r.desc}</span>
              <span className="exc-issue">Quoted in {r.uomNorm || '—'}, billed in {r.invoicedUOM || '—'}</span>
            </ExcItem>
          ))}
        </ExcGroup>
      )}

      {b.review.length > 0 && (
        <ExcGroup tone="warn" title="Double-check these matches" count={b.review.length}
          note="We paired these automatically but aren't fully sure they're the same item.">
          {b.review.map((r) => {
            const inv = r.breakdown?.[0]?.srcItem;
            const canConfirm = !!r.quoteItem && !!inv;
            return (
              <ExcItem key={r.key} {...srcProps(r)} actions={<>
                {canConfirm && <button className="exc-btn exc-btn-primary" onClick={() => onConfirm(r)}>Yes, same item</button>}
                <button className="exc-btn" onClick={onOpenAudit}>See details</button>
              </>}>
                <span className="exc-desc" title={r.desc}>{r.desc}</span>
                {inv && <span className="exc-alias" title={inv.desc}>↔ {inv.desc}</span>}
              </ExcItem>
            );
          })}
        </ExcGroup>
      )}
    </div>
  );
}

// ── ToleranceSettingsPanel ──────────────────────────────────────────────────
// Module scope, not inside the panel: a component defined during render is a
// new type every render, so React remounted the input on each keystroke and
// the field lost focus after one character.
function ToleranceField({ label, field, suffix, value, onChange }) {
  const id = `tsp-${field}`;
  return (
    <div className="tsp-field">
      <label className="tsp-label" htmlFor={id}>{label}</label>
      <div className="tsp-input-wrap">
        <input
          id={id}
          type="number" min="0" step={suffix === '%' ? '0.5' : '50'}
          className="input tsp-input"
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
        />
        <span className="tsp-suffix">{suffix}</span>
      </div>
    </div>
  );
}

function ToleranceSettingsPanel({ tolerances, onSave, onSaveGlobal, onClose }) {
  const [local, setLocal] = React.useState({ ...DEFAULT_TOLERANCES, ...tolerances });
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: +v || 0 }));

  return (
    <div className="tsp card">
      <div className="tsp-hd">
        <div>
          <div className="tsp-tag mono">TOLERANCE SETTINGS</div>
          <div className="tsp-title">Flag thresholds for this job</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><Icon name="x" size={13}/></button>
      </div>
      <div className="tsp-body">
        <ToleranceField label="Quantity tolerance" field="quantity_tolerance_pct" suffix="%" value={local.quantity_tolerance_pct} onChange={set} />
        <ToleranceField label="Price tolerance (percent)" field="price_tolerance_pct" suffix="%" value={local.price_tolerance_pct} onChange={set} />
        <ToleranceField label="Price tolerance (flat)" field="price_tolerance_flat" suffix="$" value={local.price_tolerance_flat} onChange={set} />
        <ToleranceField label="Footage tolerance (LF/SF/SY)" field="footage_tolerance_pct" suffix="%" value={local.footage_tolerance_pct} onChange={set} />
        <div className="tsp-hint">
          Items are flagged when <em>either</em> the % or flat $ threshold is exceeded.
          Exceeding both marks as CRITICAL.
        </div>
      </div>
      <div className="tsp-foot">
        <button className="btn btn-ghost btn-sm" onClick={() => onSaveGlobal && onSaveGlobal(local)}>
          Set as global default
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => { onSave(local); onClose(); }}>
          Save for this job
        </button>
      </div>
    </div>
  );
}

// ── VarianceFilterBar ───────────────────────────────────────────────────────
// Filter tabs are fixed copy with no local state, so the list lives at module
// scope and stays a stable reference across renders.
const STATUS_OPTS = [
  { key: 'all',     label: 'All' },
  { key: 'problem', label: 'Problem' },
  { key: 'review',  label: 'Needs Review' },
  { key: 'good',    label: 'Good' },
];

function VarianceFilterBar({ filter, onFilter, counts, onToggleFlaggedOnly, flaggedOnly }) {
  return (
    <div className="vfb">
      <div className="vfb-filters">
        {STATUS_OPTS.map(({ key, label }) => (
          <button key={key} className={`vfb-btn ${filter === key ? 'is-on' : ''}`} onClick={() => onFilter(key)}>
            {label}
            <span className="vfb-count num">{key === 'all' ? counts.total : (counts[key] || 0)}</span>
          </button>
        ))}
      </div>
      <button className={`vfb-toggle ${flaggedOnly ? 'is-on' : ''}`} onClick={onToggleFlaggedOnly}>
        <Icon name="flag" size={12} /> Flagged only
      </button>
    </div>
  );
}

// ── VarianceLineTable ───────────────────────────────────────────────────────
// Module scope so the button keeps its identity across renders — defined inside
// the table it was remounted on every sort, and the click that sorted the
// column also threw keyboard focus off it.
function SortBtn({ k, label, sortKey, sortDir, onSort }) {
  return (
    <button className={`vlt-sort-btn ${sortKey === k ? 'is-on' : ''}`} onClick={() => onSort(k)}>
      {label}{sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
}

function VarianceLineTable({ rows, states, filter, flaggedOnly, sortKey, sortDir, onSort }) {
  const lvl = React.useCallback((x) => (states.get(x)?.level) || 'good', [states]);
  const filtered = React.useMemo(() => {
    let r = rows.filter((x) => !x.synthetic);
    if (filter && filter !== 'all') r = r.filter((x) => lvl(x) === filter);
    if (flaggedOnly) r = r.filter((x) => lvl(x) !== 'good');
    return r;
  }, [rows, filter, flaggedOnly, lvl]);

  const sorted = React.useMemo(() => {
    if (!sortKey) return filtered;
    return filtered.toSorted((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'absDelta') { av = Math.abs(a.delta); bv = Math.abs(b.delta); }
      if (sortKey === 'deltaPct') {
        av = a.quotedTotal > 0 ? Math.abs(a.delta / a.quotedTotal) : 0;
        bv = b.quotedTotal > 0 ? Math.abs(b.delta / b.quotedTotal) : 0;
      }
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [filtered, sortKey, sortDir]);

  if (sorted.length === 0) {
    return (
      <div className="vlt-empty">
        <Icon name="check" size={20} />
        <span>No items match this filter.</span>
      </div>
    );
  }

  return (
    /* UX #1 (a11y): expose the div-based variance grid as an ARIA table so the
       row/column structure is available to assistive tech (layout unchanged). */
    <div className="vlt" role="table" aria-label="Line-item variances">
      <div className="vlt-head" role="row">
        <div className="vlt-h vlt-h-desc" role="columnheader">Description</div>
        <div className="vlt-h vlt-h-qty" role="columnheader">Qtd qty</div>
        <div className="vlt-h vlt-h-qty" role="columnheader">Inv qty</div>
        <div className="vlt-h vlt-h-qty" role="columnheader">Qty Δ%</div>
        <div className="vlt-h vlt-h-num" role="columnheader">Quoted $</div>
        <div className="vlt-h vlt-h-num" role="columnheader">Invoiced $</div>
        <div className="vlt-h vlt-h-num" role="columnheader">
          <SortBtn k="absDelta" label="$ Δ" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
        </div>
        <div className="vlt-h vlt-h-num" role="columnheader">
          <SortBtn k="deltaPct" label="Δ %" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
        </div>
        <div className="vlt-h vlt-h-st" role="columnheader">Status</div>
      </div>

      <div className="vlt-body" role="rowgroup">
        {sorted.map((r) => {
          // Units that can't be reconciled have no meaningful dollar variance.
          const deltaPct = (!r.flags.uom && r.quotedTotal > 0) ? r.delta / r.quotedTotal * 100 : null;
          const qtyDeltaPct = (r.quotedQty > 0 && r.invoicedQty != null)
            ? (r.invoicedQty - r.quotedQty) / r.quotedQty * 100
            : null;
          const st = states.get(r);
          const tone = LEVEL_BADGE[st?.level]?.tone || 'ok';

          const showBoth = r.invoiceDesc
            && r.invoiceDesc.toLowerCase() !== r.desc.toLowerCase()
            && r.matchType !== 'new_charge'
            && r.matchType !== 'missing_from_invoice';
          // Show WHY it matched + the confidence the auto-matcher assigned, so
          // estimators can trust matches at a glance instead of hand-reviewing.
          const confSuffix = r.confidence != null ? ` ${r.confidence}%` : '';
          const lowSuffix  = r.lowConfidence ? ' · low' : '';
          const badgeLabel = r.matchType === 'new_charge'        ? 'new material'
            : r.matchType === 'manual'                            ? 'manual'
            : r.matchReason === 'learned_alias'                   ? 'linked'
            : r.matchReason === 'exact'                           ? null /* exact: no clutter */
            : r.matchType === 'partial'                           ? `partial${confSuffix}${lowSuffix} — ${r.matchNote || 'size differs'}`
            : r.matchReason                                       ? `${r.matchReason}${confSuffix}${lowSuffix}`
            : r.matchType === 'fuzzy_desc' && r.matchScore === 0  ? 'normalized'
            : r.matchType === 'fuzzy_desc'                        ? 'fuzzy'
            : r.matchType === 'cost_code'                         ? 'cost code'
            : r.matchType === 'alias'                             ? 'linked'
            : null;
          const badgeClass = r.matchType === 'new_charge'        ? 'vlt-new'
            : r.lowConfidence                                     ? 'vlt-low'
            : r.matchReason === 'normalized' || (r.matchType === 'fuzzy_desc' && r.matchScore === 0) ? 'vlt-norm'
            : r.matchType === 'partial'                           ? 'vlt-partial'
            : '';

          return (
            <div key={r.key} className={`vlt-row vlt-row-${tone}`} role="row">
              <div className="vlt-c vlt-c-desc" role="cell">
                <div className="vlt-desc-main">
                  {showBoth ? (
                    <div className="vlt-desc-stack">
                      <div className="vlt-desc-pair">
                        <span className="vlt-desc-src vlt-desc-src-q">QT</span>
                        <span className="vlt-desc" title={r.desc}>{r.desc}</span>
                      </div>
                      <div className="vlt-desc-pair">
                        <span className="vlt-desc-src vlt-desc-src-i">INV</span>
                        <span className="vlt-desc vlt-desc-inv" title={r.invoiceDesc}>{r.invoiceDesc}</span>
                      </div>
                    </div>
                  ) : (
                    <span className="vlt-desc" title={r.desc}>{r.desc}</span>
                  )}
                </div>
                <div className="vlt-desc-meta">
                  {r.sku !== '—' && <span className="vlt-sku mono">{r.sku}</span>}
                  {badgeLabel && <span className={`vlt-match-badge ${badgeClass}`}>{badgeLabel}</span>}
                </div>
              </div>
              <div className="vlt-c vlt-c-qty num" role="cell">
                {r.quotedQty != null ? r.quotedQty.toLocaleString() : <span className="vlt-dash">—</span>}
                {r.uomNorm && <span className="vlt-uom"> {r.uomNorm}</span>}
              </div>
              <div role="cell" className={`vlt-c vlt-c-qty num ${r.flags.qty || r.flags.over || r.flags.uom ? 'vlt-flag' : ''}`}>
                {r.invoicedQty > 0 ? r.invoicedQty.toLocaleString() : <span className="vlt-dash">—</span>}
                {/* unreconciled units: label the invoiced qty with ITS unit */}
                {r.flags.uom && r.invoicedUOM && <span className="vlt-uom"> {r.invoicedUOM}</span>}
              </div>
              <div role="cell" className={`vlt-c vlt-c-qty num ${qtyDeltaPct != null && Math.abs(qtyDeltaPct) > 0 ? 'vlt-flag' : ''}`}>
                {qtyDeltaPct != null && !r.flags.uom ? (qtyDeltaPct >= 0 ? '+' : '') + qtyDeltaPct.toFixed(1) + '%' : '—'}
              </div>
              <div className="vlt-c vlt-c-num num" role="cell">
                {r.quotedTotal > 0 ? fmtUSD(r.quotedTotal) : <span className="vlt-dash">—</span>}
              </div>
              <div className="vlt-c vlt-c-num num" role="cell">
                {r.invoicedTotal > 0 ? fmtUSD(r.invoicedTotal) : <span className="vlt-dash">—</span>}
              </div>
              <div role="cell" className={`vlt-c vlt-c-num num ${!r.flags.uom && r.delta > 0 ? 'vlt-pos' : !r.flags.uom && r.delta < 0 ? 'vlt-neg' : ''}`}>
                {r.flags.uom ? <span className="vlt-dash">—</span> : r.delta !== 0 ? fmtDelta(r.delta) : '—'}
              </div>
              <div role="cell" className={`vlt-c vlt-c-num num ${deltaPct != null && Math.abs(deltaPct) > 0 ? (r.delta > 0 ? 'vlt-pos' : 'vlt-neg') : ''}`}>
                {deltaPct != null && deltaPct !== 0 ? (deltaPct >= 0 ? '+' : '') + deltaPct.toFixed(1) + '%' : '—'}
              </div>
              <div className="vlt-c vlt-c-st" role="cell">
                <LevelBadge state={st} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── FootageReconcTable ──────────────────────────────────────────────────────
function FootageReconcTable({ rows }) {
  // Rows billed directly by footage (LF/SF/SY) plus rows whose footage is
  // derived from stick count × stick length ("12 LN of 20 FT tube" → 240 LF).
  const footageRows = rows.flatMap((r) => {
    if (r.flags.added || !(FOOTAGE_UOMS.has(r.uomNorm) || r.quotedLF != null)) return [];
    const derived = !FOOTAGE_UOMS.has(r.uomNorm);
    return [{
      ...r,
      _ftDerived:  derived,
      _ftQuoted:   derived ? (r.quotedLF   || 0) : (r.quotedQty   || 0),
      _ftInvoiced: derived ? (r.invoicedLF || 0) : (r.invoicedQty || 0),
    }];
  });
  if (footageRows.length === 0) return null;

  const totals = footageRows.reduce(
    (s, r) => ({
      quoted:   s.quoted   + r._ftQuoted,
      invoiced: s.invoiced + r._ftInvoiced,
    }),
    { quoted: 0, invoiced: 0 }
  );
  totals.delta = totals.invoiced - totals.quoted;

  return (
    <div className="frt card">
      <div className="frt-hd">
        <Icon name="arrow" size={14} />
        <span className="frt-title">Footage Reconciliation</span>
        <span className="frt-sub">Items billed by linear / square measure — LF* is derived from stick count × length</span>
      </div>
      {/* UX #1 (a11y): same ARIA table treatment as the main variance grid. */}
      <div className="frt-table" role="table" aria-label="Footage reconciliation">
        <div className="frt-head" role="row">
          <div className="frt-h frt-h-desc" role="columnheader">Description</div>
          <div className="frt-h frt-h-uom" role="columnheader">UOM</div>
          <div className="frt-h frt-h-qty" role="columnheader">Quoted</div>
          <div className="frt-h frt-h-qty" role="columnheader">Invoiced</div>
          <div className="frt-h frt-h-qty" role="columnheader">Delta</div>
          <div className="frt-h frt-h-qty" role="columnheader">Delta %</div>
          <div className="frt-h frt-h-st" role="columnheader">Status</div>
        </div>
        {footageRows.map((r) => {
          const delta   = r._ftInvoiced - r._ftQuoted;
          const deltaPct = r._ftQuoted > 0 ? delta / r._ftQuoted * 100 : 0;
          return (
            <div key={r.key} className="frt-row" role="row">
              <div className="frt-c frt-c-desc" role="cell" title={r.desc}>{r.desc}</div>
              <div className="frt-c frt-c-uom mono" role="cell"
                title={r._ftDerived ? `Derived from ${r.uomNorm} sticks × length in description` : undefined}>
                {r._ftDerived ? 'LF*' : r.uomNorm}
              </div>
              <div className="frt-c frt-c-qty num" role="cell">{r._ftQuoted > 0 ? r._ftQuoted.toLocaleString() : '—'}</div>
              <div className="frt-c frt-c-qty num" role="cell">{r._ftInvoiced > 0 ? r._ftInvoiced.toLocaleString() : '—'}</div>
              <div role="cell" className={`frt-c frt-c-qty num ${delta > 0 ? 'frt-pos' : delta < 0 ? 'frt-neg' : ''}`}>
                {delta !== 0 ? (delta > 0 ? '+' : '') + delta.toLocaleString() : '—'}
              </div>
              <div role="cell" className={`frt-c frt-c-qty num ${Math.abs(deltaPct) > 0 ? (delta > 0 ? 'frt-pos' : 'frt-neg') : ''}`}>
                {deltaPct !== 0 ? (deltaPct > 0 ? '+' : '') + deltaPct.toFixed(1) + '%' : '—'}
              </div>
              <div className="frt-c frt-c-st" role="cell"><VarianceBadge status={r.varianceStatus} /></div>
            </div>
          );
        })}
        <div className="frt-foot" role="row">
          <div className="frt-f frt-f-lbl" role="cell">Totals</div>
          <div className="frt-f frt-f-qty num" role="cell">{totals.quoted.toLocaleString()}</div>
          <div className="frt-f frt-f-qty num" role="cell">{totals.invoiced.toLocaleString()}</div>
          <div role="cell" className={`frt-f frt-f-qty num ${totals.delta > 0 ? 'frt-pos' : totals.delta < 0 ? 'frt-neg' : ''}`}>
            {totals.delta !== 0 ? (totals.delta > 0 ? '+' : '') + totals.delta.toLocaleString() : '—'}
          </div>
          <div className="frt-f" role="cell" />
          <div className="frt-f" role="cell" />
        </div>
      </div>
    </div>
  );
}

// ── VarianceSection ───────────────────────────────────────────────────────
// One trade's issues: exposure banner, exceptions desk, full line table, and
// (single-trade only) the match audit. quote/invoices are already scoped to the
// trade; vendor rules resolve from this segment's own quote vendor.
function VarianceSection({ trade, showHeader, quote, invoices, tolerances,
                           vendorRules, setVendorRules, job, userId }) {
  const [filter, setFilter]       = React.useState('all');
  const [flaggedOnly, setFlagged] = React.useState(false);
  const [sortKey, setSortKey]     = React.useState('absDelta');
  const [sortDir, setSortDir]     = React.useState('desc');
  const [auditOpen, setAuditOpen] = React.useState(false);
  const [preview, setPreview]     = React.useState(null); // { doc, sel } for the popup PDF viewer
  const auditRef = React.useRef(null);
  const openPreview = React.useCallback((doc, sel) => setPreview({ doc, sel }), []);

  const vendor = quote?.vendor || '';

  // Resolve a low-confidence / unmatched pairing by saving it as a learned alias
  // — it becomes a confident "Linked" match for this vendor on every future job.
  const confirmMatch = React.useCallback((row) => {
    const inv = row.breakdown?.[0]?.srcItem;
    if (!setVendorRules || !row.quoteItem || !inv) return;
    const label = (it) => (it.sku && !isAutoSku(it.sku)) ? it.sku : (it.desc || '').slice(0, 40);
    const rule = {
      id: 'r' + Date.now(), kind: 'alias', added: new Date().toISOString().slice(0, 10),
      quoteKey: itemKeyOf(row.quoteItem), invoiceKey: itemKeyOf(inv),
      pattern: `${label(row.quoteItem)} ↔ ${label(inv)}`,
      note: `Confirmed from Issues — "${row.quoteItem.desc}" ↔ "${inv.desc}".`,
    };
    setVendorRules((prev) => ({ ...prev, [vendor]: [...((prev || {})[vendor] || []), rule] }));
  }, [setVendorRules, vendor]);

  // Accept an exception as-is (stop flagging it). Keyed to the item so it's
  // precise even when the line has no real SKU.
  const acceptItem = React.useCallback((row) => {
    if (!setVendorRules) return;
    const item = row.quoteItem || row.breakdown?.[0]?.srcItem;
    if (!item) return;
    const rule = {
      id: 'r' + Date.now(), kind: 'ignore', added: new Date().toISOString().slice(0, 10),
      quoteKey: itemKeyOf(item),
      pattern: (row.sku && row.sku !== '—') ? row.sku : '(item)',
      note: `Accepted from Issues — "${row.desc}".`,
    };
    setVendorRules((prev) => ({ ...prev, [vendor]: [...((prev || {})[vendor] || []), rule] }));
  }, [setVendorRules, vendor]);

  const openAudit = React.useCallback(() => {
    setAuditOpen(true);
    requestAnimationFrame(() => auditRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, []);

  const handleSort = (k) => {
    if (sortKey === k) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  // Vendor rules (alias links etc.) for the quoted vendor feed the matcher so
  // this view pairs lines exactly like the budget tracker does.
  const rules = React.useMemo(
    () => (vendorRules || {})[quote?.vendor || ''] || [],
    [vendorRules, quote?.vendor]
  );

  const result = React.useMemo(
    () => quote && invoices.length > 0 ? computeVariances(quote, invoices, tolerances, rules) : null,
    [quote, invoices, tolerances, rules]
  );

  // ONE classification for the whole section — banner, exceptions, filters, and
  // table all read from this, so the counts can't disagree with each other.
  const classified = React.useMemo(
    () => result ? classifyRows(result.rows, tolerances) : null,
    [result, tolerances]
  );

  const counts = React.useMemo(() => {
    if (!classified) return { total: 0, problem: 0, review: 0, good: 0 };
    return { total: classified.real.length, ...classified.count };
  }, [classified]);

  const header = showHeader ? (
    <TradeSectionHeader trade={trade}
      sub={classified ? `${classified.count.problem + classified.count.review} to resolve` : null} />
  ) : null;

  if (!quote) {
    return <>{header}<div className="vv-section-empty">No quote loaded for {trade}.</div></>;
  }
  if (invoices.length === 0) {
    return <>{header}<div className="vv-section-empty">No invoices filed under {trade} yet.</div></>;
  }

  return (
    <>
      {header}
      {result && classified && (
        <>
          <VarianceSummaryBanner
            problem={classified.count.problem}
            review={classified.count.review}
            exposure={classified.exposure}
          />

          {/* The exceptions desk — everything needing a human, in one place. */}
          <ExceptionsPanel
            rows={result.rows}
            states={classified.states}
            quote={quote} invoices={invoices} onPreview={openPreview}
            onConfirm={confirmMatch}
            onAccept={acceptItem}
            onOpenAudit={openAudit}
          />

          {/* Full line-item detail, still filterable, below the triage. */}
          <details className="vv-disc">
            <summary className="vv-disc-sum">
              <Icon name="rule" size={14} />
              <span>See every line (detailed view)</span>
              <Icon name="chev" size={13} className="vv-disc-chev" />
            </summary>
            <div className="vv-disc-body">
              <VarianceFilterBar
                filter={filter} onFilter={setFilter}
                counts={counts}
                flaggedOnly={flaggedOnly} onToggleFlaggedOnly={() => setFlagged((v) => !v)}
              />
              <VarianceLineTable
                rows={result.rows}
                states={classified.states}
                filter={filter} flaggedOnly={flaggedOnly}
                sortKey={sortKey} sortDir={sortDir} onSort={handleSort}
              />
              <FootageReconcTable rows={result.rows} />
            </div>
          </details>

          {/* Diagnostics: how each line was paired (single-trade only — the audit
              reads the job's primary quote and can't yet be scoped per trade). */}
          {!showHeader && (
            <details className="vv-disc" ref={auditRef} open={auditOpen}
                     onToggle={(e) => setAuditOpen(e.currentTarget.open)}>
              <summary className="vv-disc-sum">
                <Icon name="search" size={14} />
                <span>Match audit — how each line was paired</span>
                <Icon name="chev" size={13} className="vv-disc-chev" />
              </summary>
              <div className="vv-disc-body">
                <MatchAuditView
                  job={job}
                  userId={userId}
                  vendorRules={vendorRules}
                  setVendorRules={setVendorRules}
                  embedded
                />
              </div>
            </details>
          )}
        </>
      )}

      {preview && (
        <DocProvenanceModal invoice={preview.doc} initialSel={preview.sel}
          onClose={() => setPreview(null)} />
      )}
    </>
  );
}

// ── VarianceView ─────────────────────────────────────────────────────────────
function VarianceView({ job, userId, vendorRules, setVendorRules, selectedTrades }) {
  const [quote, setQuote]         = React.useState(null);
  const [quotesByTrade, setQuotesByTrade] = React.useState({});
  const [trades, setTrades]       = React.useState(() => getJobTrades(job));
  const [invoices, setInvoices]   = React.useState([]);
  const [synced, setSynced]       = React.useState(false);
  const [tolerances, setTols]     = React.useState(DEFAULT_TOLERANCES);

  const isSample = job.id === '_sample';

  // Live job doc + invoices via the shared dual-shape hook (invodiff-data.jsx).
  // Previously this view did a one-shot .get() — the only in-job screen that
  // didn't reflect teammate edits without a refresh; the hook makes it live.
  const live = useJobData(isSample ? null : job.id);
  React.useEffect(() => {
    if (isSample) {
      setQuote(INVOICE_A);
      setInvoices([INVOICE_B]);
      setSynced(true);
      return;
    }
    if (!live.synced) { setSynced(false); return; }
    if (live.exists) {
      const d = live.data;
      setQuote(d.quote ?? null);
      setQuotesByTrade(d.quotesByTrade ?? {});
      setTrades(getJobTrades(d));
      setInvoices(live.invoices);
      setTols({ ...DEFAULT_TOLERANCES, ...(d.tolerances || {}) });
    }
    setSynced(true);
  }, [isSample, live]);

  const multiTrade = trades.length > 1;
  const segments = React.useMemo(
    () => getSegments({ quote, invoices, quotesByTrade, trades }, selectedTrades),
    [quote, invoices, quotesByTrade, trades, selectedTrades]
  );
  const anyQuote = segments.some((s) => s.quote);
  const anyInv   = segments.some((s) => s.invoices.length > 0);

  if (!synced) {
    return (
      <div className="vv-loading">
        <div className="vv-spin" />
        <VarianceEngineStyles />
      </div>
    );
  }

  if (!anyQuote) {
    return (
      <div className="vv vv-empty">
        <div className="vv-empty-icon"><Icon name="delta" size={32} /></div>
        <div className="vv-empty-h">No quote loaded</div>
        <div className="vv-empty-sub">Upload a quote in the Invoices tab and issues will be flagged here.</div>
        <VarianceEngineStyles />
      </div>
    );
  }

  if (!anyInv) {
    return (
      <div className="vv vv-empty">
        <div className="vv-empty-icon"><Icon name="file" size={32} /></div>
        <div className="vv-empty-h">No invoices yet</div>
        <div className="vv-empty-sub">Add invoices in the Invoices tab — anything off will show up here.</div>
        <VarianceEngineStyles />
      </div>
    );
  }

  return (
    <div className="vv">
      <div className="vv-hero">
        <div className="vv-hero-tag mono">JOB {job.jobNumber} · ISSUES</div>
        <h1 className="vv-hero-h">Issues</h1>
        <p className="vv-hero-sub">
          Anything an invoice charged that doesn't match your quote shows up here, with what to do about it.
          Small differences within range are cleared automatically.
        </p>
      </div>

      {segments.map((seg) => (
        <VarianceSection
          key={`${job.id}:${seg.trade || '_single'}`} trade={seg.trade} showHeader={multiTrade}
          quote={seg.quote} invoices={seg.invoices} tolerances={tolerances}
          vendorRules={vendorRules} setVendorRules={setVendorRules}
          job={job} userId={userId}
        />
      ))}

      <VarianceEngineStyles />
    </div>
  );
}

const VarianceEngineStyles = () => (
  <style>{`
    /* ── Loading / empty ─────────────────────────────────────────── */
    .vv { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 40px var(--app-pad) 80px; display: flex; flex-direction: column; gap: 18px; }
    .vv-loading { display: flex; align-items: center; justify-content: center; min-height: 60vh; }
    .vv-spin { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }
    .vv-empty { align-items: center; justify-content: center; min-height: 40vh; text-align: center; }
    .vv-empty-icon { color: var(--fg-dim); opacity: 0.3; }
    .vv-empty-h   { font-size: 15px; font-weight: 500; color: var(--fg-muted); }
    .vv-empty-sub { font-size: 13px; color: var(--fg-dim); max-width: 340px; line-height: 1.55; }
    .vv-section-empty { padding: 24px; text-align: center; color: var(--fg-dim); font-size: 13px;
      background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); }

    /* ── Diagnostics disclosure (Match audit) ───────────────────── */
    .vv-disc { margin-top: 6px; border: 1px solid var(--border); border-radius: var(--r-lg); background: var(--surface); overflow: hidden; }
    .vv-disc[open] { box-shadow: var(--shadow-1); }
    .vv-disc-sum {
      display: flex; align-items: center; gap: 10px; cursor: pointer; list-style: none;
      padding: 14px 18px; font-size: 13.5px; font-weight: 600; color: var(--fg-muted);
      user-select: none; transition: color .12s, background .12s;
    }
    .vv-disc-sum::-webkit-details-marker { display: none; }
    .vv-disc-sum:hover { color: var(--fg); background: var(--surface-2); }
    .vv-disc-sum > svg:first-child { color: var(--accent); }
    .vv-disc-chev { margin-left: auto; color: var(--fg-dim); transition: transform .15s; }
    .vv-disc[open] .vv-disc-chev { transform: rotate(90deg); }
    .vv-disc-body { padding: 0 18px 18px; border-top: 1px solid var(--border); }

    /* ── Exceptions desk ─────────────────────────────────────────── */
    .exc { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); box-shadow: var(--shadow-1); overflow: hidden; }
    .exc-clear { display: flex; align-items: center; gap: 12px; padding: 18px 20px; }
    .exc-clear svg { color: var(--ok); flex-shrink: 0; }
    .exc-clear-h { font-size: 14px; font-weight: 600; }
    .exc-clear-s { font-size: 12.5px; color: var(--fg-muted); margin-top: 2px; }
    .exc-hd { display: flex; align-items: center; gap: 9px; padding: 13px 18px; border-bottom: 1px solid var(--border); background: var(--surface-2); }
    .exc-hd svg { color: var(--warn); }
    .exc-hd-t { font-size: 13.5px; font-weight: 700; letter-spacing: -0.01em; }
    .exc-hd-c { font-size: 11px; font-weight: 600; padding: 1px 8px; border-radius: 999px; background: var(--warn-bg); color: var(--warn); }
    .exc-hd-note { font-size: 12px; color: var(--fg-dim); margin-left: auto; }

    .exc-grp { border-bottom: 1px solid var(--border); }
    .exc-grp:last-child { border-bottom: none; }
    .exc-grp-hd { display: flex; align-items: center; gap: 8px; padding: 11px 18px 7px; }
    .exc-grp-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
    .exc-grp-bad  .exc-grp-dot { background: var(--bad); }
    .exc-grp-warn .exc-grp-dot { background: var(--warn); }
    .exc-grp-t { font-size: 12.5px; font-weight: 600; }
    .exc-grp-c { font-size: 10.5px; font-weight: 600; padding: 0 7px; border-radius: 999px; background: var(--surface-3); color: var(--fg-muted); }
    .exc-grp-note { font-size: 11.5px; color: var(--fg-dim); margin-left: 4px; }
    .exc-grp-body { padding: 0 10px 8px; display: flex; flex-direction: column; gap: 2px; }

    .exc-item { display: flex; align-items: center; gap: 12px; padding: 8px 8px; border-radius: var(--r); }
    .exc-item:hover { background: var(--surface-2); }
    .exc-item-main { display: flex; align-items: baseline; gap: 10px; min-width: 0; flex: 1; flex-wrap: wrap; }
    .exc-desc { font-size: 13px; font-weight: 500; color: var(--fg); max-width: 340px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .exc-sku { font-size: 10px; color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border); padding: 0 5px; border-radius: 3px; }
    .exc-alias { font-size: 12px; color: var(--fg-muted); font-style: italic; max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .exc-amt, .exc-qty { font-size: 12px; color: var(--fg-muted); font-variant-numeric: tabular-nums; }
    .exc-delta { font-size: 12.5px; font-weight: 700; color: var(--bad); font-variant-numeric: tabular-nums; }
    .exc-issue { font-size: 11px; font-weight: 600; color: var(--warn); background: var(--warn-bg);
      border-radius: 4px; padding: 1px 7px; white-space: nowrap; }
    .exc-item-acts { display: flex; gap: 6px; flex-shrink: 0; }
    /* Look at the document before deciding — visually distinct from the
       resolve actions so it doesn't read as a third way to clear the row. */
    .exc-btn-src { display: inline-flex; align-items: center; gap: 5px; color: var(--accent); border-color: oklch(from var(--accent) l calc(c*.5) h / .4); }
    .exc-btn-src:hover { background: oklch(from var(--accent) l c h / .12); border-color: var(--accent); }
    .exc-btn { appearance: none; border: 1px solid var(--border-strong); background: var(--surface-2); color: var(--fg-muted);
      font: inherit; font-size: 12px; font-weight: 500; padding: 5px 11px; border-radius: var(--r); cursor: pointer; white-space: nowrap;
      transition: background .12s, color .12s, border-color .12s; }
    .exc-btn:hover { background: var(--surface-3); color: var(--fg); }
    .exc-btn-primary { background: oklch(from var(--accent) l c h / 0.14); color: var(--accent); border-color: oklch(from var(--accent) l calc(c*.5) h / .5); font-weight: 600; }
    .exc-btn-primary:hover { background: oklch(from var(--accent) l c h / 0.22); color: var(--accent); }
    @media (max-width: 640px) {
      .exc-item { flex-direction: column; align-items: stretch; }
      .exc-item-acts { justify-content: flex-end; }
    }

    /* ── Hero ────────────────────────────────────────────────────── */
    .vv-hero-tag { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
    .vv-hero-h   { font-size: 25px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 6px; }
    .vv-hero-sub { font-size: 14.5px; color: var(--fg-muted); margin: 0; line-height: 1.55; max-width: 560px; }

    /* ── VarianceBadge ───────────────────────────────────────────── */
    .vbadge {
      display: inline-flex; align-items: center;
      height: 20px; padding: 0 8px; border-radius: 999px;
      font-size: 10.5px; font-weight: 600; font-family: "IBM Plex Mono", monospace;
      letter-spacing: 0.04em; white-space: nowrap; border: 1px solid transparent;
    }
    .vbadge-ok       { background: var(--ok-bg);   color: var(--ok);   border-color: oklch(from var(--ok)   l calc(c*.5) h / .35); }
    .vbadge-warn     { background: var(--warn-bg); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.5) h / .35); }
    .vbadge-bad      { background: var(--bad-bg);  color: var(--bad);  border-color: oklch(from var(--bad)  l calc(c*.5) h / .35); }
    .vbadge-critical { background: var(--bad-bg);  color: var(--bad);  border-color: oklch(from var(--bad)  l calc(c*.5) h / .35); }
    .vbadge-pulse    { animation: vbadge-pulse 1.4s ease-in-out infinite; }
    @keyframes vbadge-pulse {
      0%, 100% { box-shadow: 0 0 0 0 oklch(from var(--bad) l c h / .4); }
      50%      { box-shadow: 0 0 0 5px oklch(from var(--bad) l c h / 0); }
    }

    /* ── Summary Banner ──────────────────────────────────────────── */
    .vsb {
      display: flex; flex-direction: column; gap: 14px;
      padding: 14px 18px; border-radius: var(--r-lg);
      border: 1px solid var(--border); background: var(--surface); box-shadow: var(--shadow-1);
    }
    .vsb-ok       { border-color: oklch(from var(--ok)   l calc(c*.5) h / .45); background: oklch(from var(--ok)   l c h / 0.04); }
    .vsb-flagged  { border-color: oklch(from var(--warn) l calc(c*.5) h / .45); background: oklch(from var(--warn) l c h / 0.04); }
    .vsb-critical { border-color: oklch(from var(--bad)  l calc(c*.5) h / .45); background: oklch(from var(--bad)  l c h / 0.04); }
    .vsb-main { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .vsb-ok-txt { font-size: 13.5px; font-weight: 500; color: var(--ok); }
    .vsb-stat { display: flex; flex-direction: column; gap: 1px; }
    .vsb-v { font-size: 22px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.1; }
    .vsb-l { font-size: 10.5px; color: var(--fg-dim); text-transform: uppercase; letter-spacing: 0.06em; }
    .vsb-sep { width: 1px; height: 30px; background: var(--border); }
    .vsb-chips { display: flex; gap: 6px; flex-wrap: wrap; }
    .vsb-chip { font-size: 10.5px; padding: 2px 9px; border-radius: 999px; font-weight: 600; border: 1px solid transparent; }
    .vsb-chip-critical { background: var(--bad-bg);  color: var(--bad);  border-color: oklch(from var(--bad)  l calc(c*.5) h / .4); }
    .vsb-chip-bad      { background: var(--bad-bg);  color: var(--bad);  border-color: oklch(from var(--bad)  l calc(c*.5) h / .35); }
    .vsb-chip-warn     { background: var(--warn-bg); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.5) h / .35); }

    /* ── Tolerance Settings Panel ────────────────────────────────── */
    .tsp { padding: 0; overflow: hidden; }
    .tsp-hd { display: flex; align-items: flex-start; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--border); }
    .tsp-tag { font-size: 10px; letter-spacing: 0.12em; color: var(--fg-dim); margin-bottom: 2px; }
    .tsp-title { font-size: 13.5px; font-weight: 600; }
    .tsp-body { padding: 14px 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; }
    .tsp-field { display: flex; flex-direction: column; gap: 5px; }
    .tsp-label { font-size: 11px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--fg-dim); }
    .tsp-input-wrap { display: flex; align-items: center; gap: 8px; }
    .tsp-input { width: 100px; height: 34px; }
    .tsp-suffix { font-size: 13px; color: var(--fg-muted); font-weight: 500; }
    .tsp-hint { grid-column: 1 / -1; font-size: 12px; color: var(--fg-dim); line-height: 1.5; font-style: italic; }
    .tsp-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid var(--border); background: var(--surface-2); }

    /* ── Filter Bar ──────────────────────────────────────────────── */
    .vfb { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
    .vfb-filters { display: flex; gap: 2px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); padding: 3px; }
    .vfb-btn { appearance: none; border: 0; background: transparent; color: var(--fg-muted); padding: 5px 10px; border-radius: 4px; font: inherit; font-size: 12.5px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
    .vfb-btn.is-on { background: var(--surface-3); color: var(--fg); }
    .vfb-btn:hover:not(.is-on) { color: var(--fg); }
    .vfb-count { font-size: 11px; background: var(--surface-2); color: var(--fg-dim); padding: 1px 6px; border-radius: 999px; }
    .vfb-btn.is-on .vfb-count { background: var(--bg); }
    .vfb-toggle { appearance: none; border: 1px solid var(--border); background: transparent; color: var(--fg-muted); padding: 5px 12px; border-radius: var(--r); font: inherit; font-size: 12.5px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .12s; }
    .vfb-toggle.is-on { background: oklch(from var(--warn) l c h / 0.12); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.5) h / .4); }
    .vfb-toggle:hover:not(.is-on) { background: var(--surface-2); color: var(--fg); }

    /* ── Variance Line Table ─────────────────────────────────────── */
    .vlt { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; box-shadow: var(--shadow-1); }
    .vlt-head, .vlt-row {
      display: grid;
      grid-template-columns: minmax(200px,1fr) 72px 72px 68px 90px 90px 90px 72px 120px;
    }
    .vlt-head { background: var(--surface-2); border-bottom: 1px solid var(--border); }
    .vlt-h { font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase; color: var(--fg-dim); font-weight: 500; padding: 8px 10px; border-right: 1px solid var(--border); }
    .vlt-h:last-child { border-right: none; }
    .vlt-h-num, .vlt-h-qty { text-align: right; }
    .vlt-sort-btn { appearance: none; border: 0; background: transparent; color: inherit; font: inherit; font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase; cursor: pointer; padding: 0; }
    .vlt-sort-btn.is-on { color: var(--accent); }

    .vlt-row { border-bottom: 1px solid var(--border); min-height: 34px; position: relative; }
    .vlt-row:last-child { border-bottom: none; }
    .vlt-row-warn::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: var(--warn); }
    .vlt-row-bad::before, .vlt-row-critical::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: var(--bad); }

    .vlt-c { padding: 0 10px; font-size: 11.5px; color: var(--fg); border-right: 1px solid var(--border); display: flex; align-items: center; min-height: 34px; }
    .vlt-c:last-child { border-right: none; }
    .vlt-c-qty, .vlt-c-num { justify-content: flex-end; font-variant-numeric: tabular-nums; font-family: "IBM Plex Mono", monospace; }
    .vlt-c-desc { flex-direction: column; align-items: flex-start; justify-content: center; padding-top: 7px; padding-bottom: 7px; gap: 4px; flex-wrap: nowrap; overflow: hidden; }
    .vlt-c-st { justify-content: flex-start; }
    .vlt-desc-main { min-width: 0; width: 100%; }
    .vlt-desc-stack { display: flex; flex-direction: column; gap: 4px; }
    .vlt-desc-pair { display: flex; align-items: baseline; gap: 5px; min-width: 0; }
    .vlt-desc-src { font-size: 9px; font-weight: 700; letter-spacing: 0.05em; flex-shrink: 0; padding: 1px 4px; border-radius: 2px; font-family: "IBM Plex Mono", monospace; }
    .vlt-desc-src-q { background: var(--surface-3); color: var(--fg-dim); }
    .vlt-desc-src-i { background: oklch(from var(--info) l c h / 0.12); color: var(--info); }
    .vlt-desc-meta { display: flex; gap: 5px; flex-wrap: wrap; align-items: center; }
    .vlt-desc { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
    .vlt-desc-inv { color: var(--fg-muted); }
    .vlt-sku  { font-size: 10px; color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border); padding: 1px 5px; border-radius: 3px; flex-shrink: 0; }
    .vlt-match-badge { font-size: 9.5px; padding: 1px 6px; border-radius: 3px; font-family: "IBM Plex Mono", monospace; flex-shrink: 0; background: var(--info-bg); color: var(--info); border: 1px solid oklch(from var(--info) l calc(c*.4) h / .3); }
    .vlt-new  { background: var(--warn-bg); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.4) h / .3); }
    .vlt-norm { background: oklch(from var(--ok) l c h / 0.12); color: var(--ok); border-color: oklch(from var(--ok) l calc(c*.5) h / .3); }
    .vlt-partial { background: var(--warn-bg); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.4) h / .3); }
    .vlt-low { background: var(--warn-bg); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.4) h / .3); }
    .vlt-dash { color: var(--fg-dim); }
    .vlt-uom  { color: var(--fg-dim); font-size: 10px; }
    .vlt-flag { color: var(--warn); font-weight: 500; }
    .vlt-pos  { color: var(--warn); }
    .vlt-neg  { color: var(--info); }
    .vlt-empty { display: flex; align-items: center; gap: 10px; padding: 28px; color: var(--fg-dim); font-size: 13.5px; justify-content: center; }

    /* ── Footage Table ───────────────────────────────────────────── */
    .frt { overflow: hidden; }
    .frt-hd { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid var(--border); }
    .frt-title { font-size: 13px; font-weight: 600; }
    .frt-sub { font-size: 12px; color: var(--fg-dim); margin-left: 4px; }
    .frt-head, .frt-row, .frt-foot { display: grid; grid-template-columns: minmax(160px,1fr) 60px 80px 80px 80px 72px 120px; }
    .frt-head { background: var(--surface-2); border-bottom: 1px solid var(--border); }
    .frt-h { font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase; color: var(--fg-dim); font-weight: 500; padding: 7px 10px; border-right: 1px solid var(--border); }
    .frt-h:last-child { border-right: none; }
    .frt-h-qty, .frt-h-uom { text-align: right; }
    .frt-row { border-bottom: 1px solid var(--border); }
    .frt-row:last-child { border-bottom: none; }
    .frt-c { padding: 0 10px; font-size: 11.5px; color: var(--fg); border-right: 1px solid var(--border); display: flex; align-items: center; min-height: 32px; }
    .frt-c:last-child { border-right: none; }
    .frt-c-qty, .frt-c-uom { justify-content: flex-end; font-variant-numeric: tabular-nums; }
    .frt-c-desc { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
    .frt-pos { color: var(--warn); }
    .frt-neg { color: var(--info); }
    .frt-foot { background: var(--surface-2); border-top: 1px solid var(--border-strong); min-height: 32px; align-items: center; }
    .frt-f { padding: 0 10px; font-size: 11.5px; font-weight: 600; font-variant-numeric: tabular-nums; }
    .frt-f-lbl { grid-column: 1 / 3; font-size: 10px; color: var(--fg-dim); text-transform: uppercase; letter-spacing: 0.06em; }
    .frt-f-qty { text-align: right; }

    @media (max-width: 900px) {
      .tsp-body { grid-template-columns: 1fr; }
      .vlt-head, .vlt-row { grid-template-columns: minmax(140px,1fr) 60px 60px 60px 80px 80px 80px 64px 110px; }
    }
  `}</style>
);

export {
  DEFAULT_TOLERANCES, VARIANCE_META, classifyVariance, computeVariances,
  VarianceBadge, VarianceSummaryBanner, ToleranceSettingsPanel,
  VarianceFilterBar, VarianceLineTable, FootageReconcTable, VarianceView,
};
